/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, OnInit } from '@angular/core';
import { AuthenticationService, Logger } from '@app/core';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { ApiQuery } from '@app/core/http/api-query';
import { EventGroup } from '@app/events/events-groups/event-group.model';
import { EventGroupsService } from '@app/events/events-groups/event-groups.service';
import { EventGroupsFormService } from '@app/events/events-groups/event-groups-form.service';
import { KendoGridComponent } from '@app/shared/kendo/kendo-grid.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { EventGroupsSeoFormService } from '@app/events/events-groups/event-groups-seo-form.service';
import { environment } from '@env/environment';
import { MediaLibraryAcceptTypes } from '@app/shared/components/media-library/media-library-accept-types.enum';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { CustomKendoFilter } from '@app/shared/kendo/custom-kendo-filter';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { AccessService } from '@app/core/access/access.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const log = new Logger('EventGroupsListComponent');

@UntilDestroy()
@Component({
  selector: 'app-event-groups-list',
  templateUrl: './event-groups-list.component.html',
  styleUrls: ['./event-groups-list.component.scss'],
})
export class EventGroupsListComponent extends KendoGridComponent implements OnInit {
  eventGroupsResponse: PaginatedResponse;
  data: GridDataResult;
  state: DataStateChangeEvent;
  apiQuery: ApiQuery = new ApiQuery();
  env = environment;
  AuthenticationService = AuthenticationService;
  MediaLibraryAcceptTypes = MediaLibraryAcceptTypes;
  LaravelModels = LaravelModelsEnum;

  UserRoleType = UserRoleType;

  constructor(
    private eventGroupsService: EventGroupsService,
    private eventGroupsFormService: EventGroupsFormService,
    private eventGroupsSeoFormService: EventGroupsSeoFormService,
    protected kendoGridService: KendoGridService,
    public accessService: AccessService
  ) {
    super(kendoGridService);
  }

  ngOnInit() {
    this.state = CustomKendoFilter.getStateFromStorage('EventGroupsListComponent');
    this.loadData(this.state);
  }

  /**
   * Load data from API
   */
  loadData(state: DataStateChangeEvent = null) {
    this.state = state;
    this.apiQuery
      .setDataSetFilters(state)
      .addIncludes('industry', 'industries', 'priority', 'repeat', 'venue.city.country', 'tags');

    this.eventGroupsService
      .index(this.apiQuery)
      .pipe(untilDestroyed(this))
      .subscribe((response: PaginatedResponse) => {
        this.eventGroupsResponse = response;
        this.data = response.getGridData();
      });
    CustomKendoFilter.pushStateInStorage('EventGroupsListComponent', this.state);
  }

  add() {
    this.eventGroupsFormService.open().then(
      (response) => {
        this.loadData();
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  /**
   *
   * @param eventGroup edit eventGroup
   */
  edit(eventGroup: EventGroup): void {
    this.eventGroupsFormService.open(eventGroup);
  }

  /**
   *  Отваря SEO формата
   * @param eventGroup Event group
   */
  editSeo(eventGroup: EventGroup): void {
    this.eventGroupsSeoFormService.open(eventGroup);
  }

  /**
   *
   * @param eventGroup delete eventGroup
   */
  delete(eventGroup: EventGroup): void {
    this.eventGroupsService
      .destroy(eventGroup.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.loadData(this.state);
      });
  }

  clearFilters() {
    this.apiQuery = new ApiQuery();
    this.loadData();
  }

  pushToStorage(group: EventGroup): void {
    CustomKendoFilter.pushObjectInStorage(group, 'event-groups');
  }
}
